import { useState, useEffect } from 'react';
import { canShowPopup, registerPopupShown, wasPopupShown } from '../../lib/popupManager';

/**
 * Triggers a popup after a delay OR when the user scrolls > 35% of the page,
 * whichever comes first. Respects the popup manager's "already shown" state
 * and a localStorage `claimedKey` so users who completed the flow are never
 * re-prompted.
 */
export function usePopupVisibility(popupId, { delayMs = 8000, scrollThreshold = 0.35, claimedKey, exitIntent = false } = {}) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // localStorage usage: non-sensitive UI state (one-time "claimed" flag)
    if (wasPopupShown(popupId) || (claimedKey && localStorage.getItem(claimedKey))) return;

    const tryShow = () => {
      if (canShowPopup(popupId)) {
        setShow(true);
        registerPopupShown(popupId);
      }
    };

    const timer = setTimeout(tryShow, delayMs);

    const handleScroll = () => {
      const scrollPct = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      if (scrollPct > scrollThreshold) {
        tryShow();
        window.removeEventListener('scroll', handleScroll);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    const handleMouseLeave = (e) => {
      // Exit intent: mouse moves to top of viewport (toward tab/url bar)
      if (e.clientY <= 0) {
        tryShow();
        document.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
    if (exitIntent) document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
      if (exitIntent) document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [popupId, delayMs, scrollThreshold, claimedKey, exitIntent]);

  return [show, setShow];
}
